import React, {lazy, useEffect, useMemo, useState} from 'react';
import {Btn, EmptyFlexDiv, FlexColumnSection, FlexDiv, FlexRowSection, SmallText} from "../../../styles/globalStyles";
import FormInput from "../../../components/UI/FormInput";
import {checkIfDateAfterToday, isLocationIL, isUserFieldRequired} from "../../../helpers/functions";
import {apiAction} from "@arboxappv4/shared/src/helpers/HTTP";
import {DB_DATE_FORMAT} from "@arboxappv4/shared/src/constants/constants";
import WhatsNextBox from "../../../components/WhatsNextBox";
import useScreenSize from "../../../helpers/hooks/useScreenSize";
import {t} from "i18next";
import {updateError} from "@arboxappv4/shared/src/redux/stepperSlice";
import {setError} from "@arboxappv4/shared/src/redux/siteSlice";
import styled from "styled-components";
import {Colors} from "@arboxappv4/shared/src/styles/Colors";
import dayjs from "dayjs";
import CustomFieldSwitcher from "../../../components/UI/CustomFieldSwitcher";
import {useDispatch, useSelector} from "react-redux";
import CustomSelect from "../../../components/UI/CustomSelect";
import {USER_SITE_FIELDS_SETTINGS} from "../../../../../src/constants";
import {CONFIRMATION_MODAL, openModal} from "@arboxappv4/shared/src/redux/modalManagerSlice";
import {customFieldFlowNames} from "@arboxappv4/shared/src/helpers/constants";
import {flowOptions} from "../../FlowOptions";

const CustomDatePicker = lazy(() => import(/* webpackChunkName: "CustomDatePicker" */'../../../components/UI/CustomDatePicker'));

const ChildDetails = (props) => {
    const { handleSubmit, values, errors, setFieldValue, touched, validationSchema, cfFlowName} = props;
    const [submitted, setSubmitted] = useState(false);
    const [errorNotified, setErrorNotified] = useState(false);
    const [personalIdError, setPersonalIdError] = useState(null);
    const [loading, setLoading] = useState(false);
    const {isMobile} = useScreenSize()
    const box = useSelector(state => state.site.box)
    const userBox = useSelector(state => state.user.activeUsersBoxes)
    const flowName = useSelector(state => state.stepper.flowName)
    const dispatch = useDispatch()
    const customFieldsDataPerLocation = useMemo(() => box?.locations_box?.find(location => location.id === values.locations_box_fk)?.custom_field, [values.locations_box_fk,box]);
    const userFieldSettings = useMemo(() => box?.boxes_settings?.find(setting => setting.prop_name === USER_SITE_FIELDS_SETTINGS)?.properties, [box])

    useEffect(() => {
        const flowCustomFields = customFieldsDataPerLocation?.filter(cf => cf?.custom_field_flow.some(cf_flow => cf_flow.flow.name === (cfFlowName)))
        const newFields = flowCustomFields?.map(field => {
            if(values.child?.customFields) {
                const old = values.child.customFields.find(oldField => oldField.id === field.id)
                if (old) {
                    return {...field, value: old.value}
                }
            }
            return field;
        })
        setTimeout(() => setFieldValue('child.customFields', newFields),10);
    }, [customFieldsDataPerLocation]);

    useEffect(() => {
        if(personalIdError) {
            setPersonalIdError(null)
        }
    }, [values.child?.personal_id]);

    useEffect(() => {
        const hasErrors = Object.keys(errors.child || {}).length > 0
        if (!hasErrors) {
            setErrorNotified(false)
            return
        }
        if (submitted && !errorNotified) {
            setErrorNotified(true)
            dispatch(setError(t('error-mandatory-field')))
        }
    }, [errors, submitted, flowName, errorNotified, dispatch]);

    const getError = (propName) => {
        if(submitted && errors.child && errors.child[propName]) {
            return errors.child[propName]
        }
        return null
    }

    const submitStep = () => {
        setSubmitted(true)
        handleSubmit()
    }

    const handleHugRestrictions = async (birthday) => {
        try {
            await apiAction('hugim/checkHugRestrictions', 'post', {series_id: values.course?.id, birthday})
            return true
        } catch(error) {
            if(error?.error?.messageToUser) dispatch(updateError(error.error.messageToUser))
            return false
        }
    }

    const handleLinkExisting = async (childUserBox) => {
        if (!(await handleHugRestrictions(childUserBox.birthday))) return
        setFieldValue('child', {users_boxes_id: childUserBox.id})
        setTimeout(() => submitStep(), 10)
    }

    const handleLinkExistingPopup = (childUserBox) => {
        dispatch(openModal({modalName: CONFIRMATION_MODAL, props: {
            header: `${childUserBox.first_name || ''} ${childUserBox.last_name || ''}`.trim(),
            explanation: t('personal-id-link-group-popup-explain'),
            submitText: t('confirm'),
            content: (
                <FlexColumnSection gap={'12px'}>
                    <SmallText>{t('personal-id')} - <b>{childUserBox.personal_id}</b></SmallText>
                    <SmallText>{t('gender')} - <b>{childUserBox.gender ? t(childUserBox.gender) : '-'}</b></SmallText>
                    <SmallText>{t('birth-date')} - <b>{childUserBox.birthday ? dayjs(childUserBox.birthday).format('DD/MM/YYYY') : '-'}</b></SmallText>
                </FlexColumnSection>
            ),
            onConfirm: () => handleLinkExisting(childUserBox),
        }}))
    }

    const handlePersonalIdGroup = async () => {
        const parentPersonalId = values.personal_id || userBox?.personal_id
        if (values.child.personal_id === parentPersonalId) {
            setPersonalIdError(t('user-box-exist-personal-id'))
            return true
        }
        // a failed lookup must not block a regular child registration
        const personalIdResult = await apiAction('site/register/personalIDGroup', 'post', {personalId: values.child.personal_id, boxId: box?.id, parentUserFk: userBox?.user_fk ?? null}).catch(() => null)
        if (personalIdResult?.userBox) {
            handleLinkExistingPopup(personalIdResult.userBox)
            return true
        }
        if (personalIdResult?.messageKey) {
            setPersonalIdError(t(personalIdResult.messageKey))
            return true
        }
        return false
    }

    const handlePersonalIdBlur = () => {
        if (values.child.personal_id && !errors.child?.personal_id) handlePersonalIdGroup()
    }

    const validatePersonalIdAndSubmit = async () => {
        setSubmitted(true)
        const childErrors = errors.child || {};
        setPersonalIdError(childErrors.personal_id || null)
        setLoading(true)
        try {
            if (isLocationIL() && values.child.personal_id && !childErrors.personal_id && await handlePersonalIdGroup()) return
            if (Object.keys(childErrors).length === 0 && !(await handleHugRestrictions(values.child.birthday))) return
            submitStep()
        } catch(error) {
            console.log('error', error)
        } finally {
            setLoading(false)
        }
    }

    const inputLabel = (name, label) => {
        // Support direct child schema and conditional child schema via when('isNewChild')
        const rawChildSchema = validationSchema?.fields?.child;
        const resolvedChildSchema = typeof rawChildSchema?.resolve === 'function'
            ? rawChildSchema.resolve({ parent: values })
            : rawChildSchema;
        const fields = resolvedChildSchema?.fields || {};
        if(resolvedChildSchema && fields && fields[name] && !fields[name].spec?.optional) {
            return `${label}*`
        } else if(isUserFieldRequired(userFieldSettings, name, 'child')) {
            return `${label}*`
        }
        return label
    }

    if(!values.child) return null

    return (
        <FlexRowSection justify={'space-between'} gap={'40px'} height={'100%'} align={'start'}>
            <FlexColumnExtended gap={'20px'} isMobile={isMobile} height={'100%'}>
                <FlexColumnSection gap={'24px'} height={isMobile ? `calc(100% - ${25+38}px)` : 'auto'} overflow={'auto'} flex={1}>
                    <FlexDiv dir={isMobile ? 'column' : 'row'} gap={'20px'}>
                        <FormInput label={inputLabel('first_name', t('first-name'))} value={values.child.first_name} error={getError('first_name')} onChangeCallback={(val) => setFieldValue('child.first_name', val)} autoComplete={'given-name'} id="firstName" flex={1}/>
                        <FormInput label={inputLabel('last_name', t('last-name'))} value={values.child.last_name} error={getError('last_name')} onChangeCallback={(val) => setFieldValue('child.last_name', val)} autoComplete={'family-name'} id="lastName" flex={1}/>
                    </FlexDiv>
                    <FlexDiv dir={isMobile ? 'column' : 'row'} gap={'20px'}>
                        {isLocationIL() && <FormInput label={inputLabel('personal_id', t('personal-id'))} value={values.child.personal_id} error={getError('personal_id') || personalIdError} onChangeCallback={(val) => setFieldValue('child', {...values.child, personal_id: val, users_boxes_id: null})} onBlur={handlePersonalIdBlur} type={'phone'} flex={1} inputMode={'tel'}/>}
                        <FormInput label={inputLabel('phone', t('child-phone'))} value={values.child.phone} error={getError('phone')} onChangeCallback={(val) => setFieldValue('child.phone', val)} type={'phone'} autoComplete={'phone'} id={'phone'} flex={1} inputMode={'tel'}/>
                    </FlexDiv>
                    <FlexDiv dir={isMobile ? 'column' : 'row'} gap={'20px'}>
                        <FormInput label={inputLabel('birthday', t('birth-date'))} value={values.child.birthday} error={getError('birthday')} flex={1} showOptional={true}>
                            <CustomDatePicker onDateSelect={(val) => setFieldValue('child.birthday', val)}
                                              disabledDate={checkIfDateAfterToday}
                                              className={`custom-date ${getError('birthday') && 'custom-date-error'}`}
                                              validRange={[dayjs('1920-01-01',DB_DATE_FORMAT), dayjs()]}
                            />
                        </FormInput>
                        <FormInput showOptional={true} label={inputLabel('gender', t('gender'))} value={values.child.gender} error={getError('gender')} flex={1}>
                            <CustomSelect className={`custom-select ${getError('gender') && 'custom-select-error'}`} allowClear filterSort={(a,b) => a - b} onChange={(val) => setFieldValue('child.gender', val)} options={[{ label: t('male'), value: 'male' }, { label: t('female'), value: 'female' }, {label: t('other'), value: 'other'}]} defaultValue={values.child.gender} />
                        </FormInput>
                    </FlexDiv>
                    {values.child.customFields && <CustomFieldSwitcher data={values.child.customFields} setFieldValue={setFieldValue} touched={touched} errors={errors} isChild/> }
                </FlexColumnSection>
                <FlexRowSection height={'38px'} flex={'none'} justify={'end'}>
                    <Btn type={'primary'} onClick={validatePersonalIdAndSubmit} width={isMobile ? '100%' : null} loading={loading}>{t('continue')}</Btn>
                </FlexRowSection>
            </FlexColumnExtended>
            {!isMobile && <WhatsNextBox text={t('course-child-details-whats-next')}/>}
        </FlexRowSection>
    );
};

export default ChildDetails;

const FlexColumnExtended = styled(FlexColumnSection)`
    padding: ${({isMobile}) => isMobile ? '0 16px' : '20px'};
    background: ${({isMobile}) => isMobile ? 'none' : Colors.white};
    box-sizing: border-box;
    border-radius: 5px;
`;


